// ═══════════════════════════════════════════════════════════════════════════
// FINANCIAL CALENDAR - UrbanQ v1.9.6
// Vista de calendario interactivo con filtros, notas y panel lateral
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    FiChevronLeft, FiChevronRight, FiDollarSign,
    FiTrendingUp, FiTrendingDown, FiCalendar, FiX,
    FiPlus, FiTrash2, FiClipboard, FiFilter
} from 'react-icons/fi';
import './FinancialCalendar.css';

const MONTHS = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const DAYS = ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'];

const FILTERS = [
    { key: 'all', label: 'Todos', color: '#667eea' },
    { key: 'income', label: 'Ingresos', color: '#48bb78' },
    { key: 'expense', label: 'Gastos', color: '#e53e3e' },
    { key: 'notes', label: 'Notas', color: '#4299e1' },
    { key: 'cashflow', label: 'Flujo de Caja', color: '#9f7aea' },
];

const NOTE_COLORS = [
    { value: '#667eea', label: 'Azul' },
    { value: '#48bb78', label: 'Verde' },
    { value: '#ed8936', label: 'Naranja' },
    { value: '#e53e3e', label: 'Rojo' },
    { value: '#9f7aea', label: 'Violeta' },
    { value: '#4299e1', label: 'Celeste' },
];

const NOTES_KEY = (userId, dateStr) => `urbanq-calendar-notes-${userId}-${dateStr}`;

export default function FinancialCalendar({ token, apiUrl, scope, onNotification, userId }) {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [incomes, setIncomes] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDay, setSelectedDay] = useState(null);
    const [activeFilter, setActiveFilter] = useState('all');
    const [panelOpen, setPanelOpen] = useState(false);
    const [notes, setNotes] = useState([]);
    const [addingNote, setAddingNote] = useState(false);
    const [newNote, setNewNote] = useState({ title: '', description: '', color: NOTE_COLORS[0].value });
    const panelRef = useRef(null);

    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const resolvedUserId = userId || 'default';

    // Cargar notas del dia seleccionado desde localStorage
    const loadNotes = useCallback((dateStr) => {
        if (!dateStr) return [];
        try {
            const raw = localStorage.getItem(NOTES_KEY(resolvedUserId, dateStr));
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    }, [resolvedUserId]);

    const saveNotes = useCallback((dateStr, notesList) => {
        try {
            localStorage.setItem(NOTES_KEY(resolvedUserId, dateStr), JSON.stringify(notesList));
        } catch {}
    }, [resolvedUserId]);

    // Obtener todas las fechas con notas en el mes actual
    const getDatesWithNotes = useCallback(() => {
        const datesWithNotes = new Set();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        for (let d = 1; d <= daysInMonth; d++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            const notesList = loadNotes(dateStr);
            if (notesList.length > 0) datesWithNotes.add(d);
        }
        return datesWithNotes;
    }, [year, month, loadNotes]);

    const [datesWithNotes, setDatesWithNotes] = useState(new Set());

    useEffect(() => {
        setDatesWithNotes(getDatesWithNotes());
    }, [getDatesWithNotes]);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [incomeRes, expenseRes] = await Promise.all([
                fetch(`${apiUrl}/api/financial/income?year=${year}&month=${month + 1}&scope=${scope}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch(`${apiUrl}/api/financial/expense?year=${year}&month=${month + 1}&scope=${scope}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
            ]);

            const incomeData = await incomeRes.json();
            const expenseData = await expenseRes.json();

            if (incomeData.success) setIncomes(incomeData.data || []);
            if (expenseData.success) setExpenses(expenseData.data || []);

        } catch (err) {
            console.error('Error fetching calendar data:', err);
            onNotification?.('Error al cargar datos del calendario', 'error');
        } finally {
            setLoading(false);
        }
    }, [year, month, token, apiUrl, scope, onNotification]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Generar dias del calendario
    const generateCalendar = () => {
        const firstDay = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const days = [];

        for (let i = 0; i < firstDay; i++) {
            days.push({ day: null });
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const dayIncomes = incomes.filter(i => i.date?.startsWith(dateStr));
            const dayExpenses = expenses.filter(e => e.date?.startsWith(dateStr));
            const totalIncome = dayIncomes.reduce((sum, i) => sum + parseFloat(i.amount || 0), 0);
            const totalExpense = dayExpenses.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
            const hasNotes = datesWithNotes.has(day);

            days.push({
                day,
                dateStr,
                incomes: dayIncomes,
                expenses: dayExpenses,
                totalIncome,
                totalExpense,
                hasIncome: dayIncomes.length > 0,
                hasExpense: dayExpenses.length > 0,
                hasNotes,
                hasData: dayIncomes.length > 0 || dayExpenses.length > 0 || hasNotes
            });
        }

        return days;
    };

    const isDayVisible = (dayObj) => {
        if (!dayObj.day) return true;
        switch (activeFilter) {
            case 'income': return dayObj.hasIncome;
            case 'expense': return dayObj.hasExpense;
            case 'notes': return dayObj.hasNotes;
            case 'cashflow': return dayObj.hasIncome || dayObj.hasExpense;
            default: return true;
        }
    };

    const navigateMonth = (direction) => {
        const newDate = new Date(currentDate);
        newDate.setMonth(newDate.getMonth() + direction);
        setCurrentDate(newDate);
        setSelectedDay(null);
        setPanelOpen(false);
    };

    const goToToday = () => {
        setCurrentDate(new Date());
        setSelectedDay(null);
        setPanelOpen(false);
    };

    const formatCurrency = (amount) => `$${parseFloat(amount || 0).toFixed(2)}`;

    const handleDayClick = (dateObj) => {
        if (!dateObj.day) return;
        if (selectedDay === dateObj.day) {
            setSelectedDay(null);
            setPanelOpen(false);
        } else {
            setSelectedDay(dateObj.day);
            setNotes(loadNotes(dateObj.dateStr));
            setPanelOpen(true);
            setAddingNote(false);
            setNewNote({ title: '', description: '', color: NOTE_COLORS[0].value });
        }
    };

    const handleAddNote = () => {
        if (!newNote.title.trim()) return;
        const dayData = calendar.find(d => d.day === selectedDay);
        if (!dayData) return;
        const updatedNotes = [...notes, {
            id: Date.now(),
            title: newNote.title.trim(),
            description: newNote.description.trim(),
            color: newNote.color,
            createdAt: new Date().toISOString()
        }];
        setNotes(updatedNotes);
        saveNotes(dayData.dateStr, updatedNotes);
        setDatesWithNotes(getDatesWithNotes());
        setAddingNote(false);
        setNewNote({ title: '', description: '', color: NOTE_COLORS[0].value });
    };

    const handleDeleteNote = (noteId) => {
        const dayData = calendar.find(d => d.day === selectedDay);
        if (!dayData) return;
        const updatedNotes = notes.filter(n => n.id !== noteId);
        setNotes(updatedNotes);
        saveNotes(dayData.dateStr, updatedNotes);
        setDatesWithNotes(getDatesWithNotes());
    };

    const calendar = generateCalendar();
    const today = new Date();
    const isToday = (day) =>
        day === today.getDate() && month === today.getMonth() && year === today.getFullYear();

    const selectedDayData = selectedDay ? calendar.find(d => d.day === selectedDay) : null;

    const monthTotals = {
        income: incomes.reduce((sum, i) => sum + parseFloat(i.amount || 0), 0),
        expense: expenses.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0)
    };
    monthTotals.balance = monthTotals.income - monthTotals.expense;

    return (
        <div className={`financial-calendar ${panelOpen ? 'panel-open' : ''}`}>
            {/* Header */}
            <div className="financial-calendar-header">
                <div className="financial-calendar-nav">
                    <button onClick={() => navigateMonth(-1)} aria-label="Mes anterior">
                        <FiChevronLeft />
                    </button>
                    <h2>
                        <FiCalendar />
                        {MONTHS[month]} {year}
                    </h2>
                    <button onClick={() => navigateMonth(1)} aria-label="Mes siguiente">
                        <FiChevronRight />
                    </button>
                </div>
                <button className="financial-calendar-today" onClick={goToToday}>
                    Hoy
                </button>
            </div>

            {/* Filtros */}
            <div className="financial-calendar-filters">
                <FiFilter className="financial-calendar-filter-icon" />
                <div className="financial-calendar-filter-chips">
                    {FILTERS.map(f => (
                        <button
                            key={f.key}
                            className={`financial-calendar-chip ${activeFilter === f.key ? 'active' : ''}`}
                            style={activeFilter === f.key ? { background: f.color, borderColor: f.color } : {}}
                            onClick={() => setActiveFilter(f.key)}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Resumen del mes */}
            <div className="financial-calendar-summary">
                <div className="financial-calendar-summary-item income">
                    <FiTrendingUp />
                    <span>Ingresos: {formatCurrency(monthTotals.income)}</span>
                </div>
                <div className="financial-calendar-summary-item expense">
                    <FiTrendingDown />
                    <span>Gastos: {formatCurrency(monthTotals.expense)}</span>
                </div>
                <div className={`financial-calendar-summary-item balance ${monthTotals.balance >= 0 ? 'positive' : 'negative'}`}>
                    <FiDollarSign />
                    <span>Balance: {formatCurrency(monthTotals.balance)}</span>
                </div>
            </div>

            <div className="financial-calendar-main">
                <div className="financial-calendar-grid-wrapper">
                    {loading ? (
                        <div className="financial-calendar-loading">
                            <div className="urbanq-finanzas-spinner"></div>
                        </div>
                    ) : (
                        <>
                            {/* Dias de la semana */}
                            <div className="financial-calendar-weekdays">
                                {DAYS.map(day => (
                                    <div key={day} className="financial-calendar-weekday">{day}</div>
                                ))}
                            </div>

                            {/* Grid de dias */}
                            <div className="financial-calendar-grid">
                                {calendar.map((dateObj, index) => {
                                    const dimmed = !isDayVisible(dateObj) && dateObj.day !== null;
                                    return (
                                        <div
                                            key={index}
                                            className={`financial-calendar-day
                                                ${dateObj.day === null ? 'empty' : ''}
                                                ${isToday(dateObj.day) ? 'today' : ''}
                                                ${selectedDay === dateObj.day ? 'selected' : ''}
                                                ${dateObj.hasData ? 'has-data' : ''}
                                                ${dimmed ? 'dimmed' : ''}
                                            `}
                                            onClick={() => handleDayClick(dateObj)}
                                        >
                                            {dateObj.day && (
                                                <>
                                                    <span className="financial-calendar-day-number">
                                                        {dateObj.day}
                                                    </span>
                                                    <div className="financial-calendar-day-dots">
                                                        {dateObj.hasIncome && (
                                                            <span className="dot dot-income" title="Ingresos"></span>
                                                        )}
                                                        {dateObj.hasExpense && (
                                                            <span className="dot dot-expense" title="Gastos"></span>
                                                        )}
                                                        {dateObj.hasNotes && (
                                                            <span className="dot dot-note" title="Notas"></span>
                                                        )}
                                                    </div>
                                                    {(activeFilter === 'all' || activeFilter === 'cashflow') && dateObj.hasData && (
                                                        <div className="financial-calendar-day-amounts">
                                                            {dateObj.totalIncome > 0 && (
                                                                <span className="amount income">
                                                                    +{formatCurrency(dateObj.totalIncome)}
                                                                </span>
                                                            )}
                                                            {dateObj.totalExpense > 0 && (
                                                                <span className="amount expense">
                                                                    -{formatCurrency(dateObj.totalExpense)}
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                    {activeFilter === 'income' && dateObj.totalIncome > 0 && (
                                                        <div className="financial-calendar-day-amounts">
                                                            <span className="amount income">
                                                                +{formatCurrency(dateObj.totalIncome)}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {activeFilter === 'expense' && dateObj.totalExpense > 0 && (
                                                        <div className="financial-calendar-day-amounts">
                                                            <span className="amount expense">
                                                                -{formatCurrency(dateObj.totalExpense)}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {activeFilter === 'notes' && dateObj.hasNotes && (
                                                        <div className="financial-calendar-day-amounts">
                                                            <span className="amount note-badge">
                                                                <FiClipboard />
                                                            </span>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>

                {/* Panel lateral */}
                <div className={`financial-calendar-side-panel ${panelOpen ? 'open' : ''}`} ref={panelRef}>
                    {selectedDayData && (
                        <>
                            <div className="financial-calendar-panel-header">
                                <h3>
                                    <FiCalendar />
                                    {selectedDay} de {MONTHS[month]} {year}
                                </h3>
                                <button
                                    className="financial-calendar-panel-close"
                                    onClick={() => { setSelectedDay(null); setPanelOpen(false); }}
                                >
                                    <FiX />
                                </button>
                            </div>

                            <div className="financial-calendar-panel-body">
                                {/* Ingresos del dia */}
                                {selectedDayData.incomes.length > 0 && (
                                    <div className="financial-calendar-panel-section">
                                        <h4 className="panel-section-title income">
                                            <FiTrendingUp /> Ingresos
                                        </h4>
                                        <ul className="financial-calendar-panel-list">
                                            {selectedDayData.incomes.map((item, i) => (
                                                <li key={i} className="panel-list-item">
                                                    <span className="item-label">{item.description || item.category_name}</span>
                                                    <span className="item-amount income">+{formatCurrency(item.amount)}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Gastos del dia */}
                                {selectedDayData.expenses.length > 0 && (
                                    <div className="financial-calendar-panel-section">
                                        <h4 className="panel-section-title expense">
                                            <FiTrendingDown /> Gastos
                                        </h4>
                                        <ul className="financial-calendar-panel-list">
                                            {selectedDayData.expenses.map((item, i) => (
                                                <li key={i} className="panel-list-item">
                                                    <span className="item-label">{item.description || item.category_name}</span>
                                                    <span className="item-amount expense">-{formatCurrency(item.amount)}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Sin movimientos */}
                                {selectedDayData.incomes.length === 0 && selectedDayData.expenses.length === 0 && (
                                    <p className="financial-calendar-panel-empty">
                                        No hay movimientos este dia
                                    </p>
                                )}

                                {/* Sección de Notas */}
                                <div className="financial-calendar-panel-section notes-section">
                                    <div className="panel-notes-header">
                                        <h4 className="panel-section-title notes">
                                            <FiClipboard /> Notas
                                        </h4>
                                        <button
                                            className="financial-calendar-add-note"
                                            onClick={() => setAddingNote(true)}
                                            disabled={addingNote}
                                        >
                                            <FiPlus /> Agregar
                                        </button>
                                    </div>

                                    {/* Formulario nueva nota */}
                                    {addingNote && (
                                        <div className="financial-calendar-note-form">
                                            <input
                                                type="text"
                                                placeholder="Título de la nota"
                                                value={newNote.title}
                                                onChange={e => setNewNote(prev => ({ ...prev, title: e.target.value }))}
                                                className="note-form-input"
                                                autoFocus
                                            />
                                            <textarea
                                                placeholder="Descripción (opcional)"
                                                value={newNote.description}
                                                onChange={e => setNewNote(prev => ({ ...prev, description: e.target.value }))}
                                                className="note-form-textarea"
                                                rows={3}
                                            />
                                            <div className="note-form-colors">
                                                {NOTE_COLORS.map(c => (
                                                    <button
                                                        key={c.value}
                                                        title={c.label}
                                                        className={`note-color-dot ${newNote.color === c.value ? 'selected' : ''}`}
                                                        style={{ background: c.value }}
                                                        onClick={() => setNewNote(prev => ({ ...prev, color: c.value }))}
                                                    />
                                                ))}
                                            </div>
                                            <div className="note-form-actions">
                                                <button
                                                    className="financial-calendar-btn secondary"
                                                    onClick={() => { setAddingNote(false); setNewNote({ title: '', description: '', color: NOTE_COLORS[0].value }); }}
                                                >
                                                    <FiX /> Cancelar
                                                </button>
                                                <button
                                                    className="financial-calendar-btn primary"
                                                    onClick={handleAddNote}
                                                    disabled={!newNote.title.trim()}
                                                >
                                                    <FiPlus /> Guardar
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Lista de notas */}
                                    {notes.length === 0 && !addingNote && (
                                        <p className="financial-calendar-panel-empty notes-empty">
                                            Sin notas para este dia
                                        </p>
                                    )}
                                    <div className="financial-calendar-notes-list">
                                        {notes.map(note => (
                                            <div
                                                key={note.id}
                                                className="financial-calendar-note-item"
                                                style={{ borderLeftColor: note.color }}
                                            >
                                                <div className="note-item-header">
                                                    <span className="note-item-title" style={{ color: note.color }}>
                                                        {note.title}
                                                    </span>
                                                    <button
                                                        className="note-item-delete"
                                                        onClick={() => handleDeleteNote(note.id)}
                                                        title="Eliminar nota"
                                                    >
                                                        <FiTrash2 />
                                                    </button>
                                                </div>
                                                {note.description && (
                                                    <p className="note-item-desc">{note.description}</p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
